import {Phone} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {DetailField} from "@/src/modules/core/components";
import {getAssigneeDisplayName} from "@/src/modules/company-on-guard/lib/assignee-utils";
import {getAssigneeTodayScheduleLines} from "@/src/modules/company-on-guard/lib/on-guard-schedule-timezone";
import type {CompanyOnGuardAssignee} from "@/src/modules/company-on-guard/types";
import {formatUsPhoneDisplay, normalizeUsPhoneDigits} from "@/src/modules/properties/lib/us-phone";

type DispatchOnGuardCallInstructionsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignee: CompanyOnGuardAssignee;
  companyTimezone?: string | null;
};

function formatTodayScheduleText(
  windows: CompanyOnGuardAssignee["windows"],
  companyTimezone?: string | null,
) {
  const lines = getAssigneeTodayScheduleLines(windows, companyTimezone);
  if (!lines.length) return null;
  return lines.map((line) => `${line.day} ${line.time}`).join(" · ");
}

export function DispatchOnGuardCallInstructionsModal({
  open,
  onOpenChange,
  assignee,
  companyTimezone,
}: DispatchOnGuardCallInstructionsModalProps) {
  const name = getAssigneeDisplayName(assignee.assignee);
  const notes = assignee.notes?.trim() ?? "";
  const scheduleText = formatTodayScheduleText(assignee.windows, companyTimezone);
  const phone = assignee.assignee.telephone;
  const phoneDisplay = formatUsPhoneDisplay(phone);
  const phoneDigits = phone ? normalizeUsPhoneDigits(phone) : "";
  const hasPhone = Boolean(phoneDigits && phoneDisplay !== "-");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-6 sm:max-w-md">
        <div className="flex flex-col gap-4">
          <DialogHeader className="gap-1 p-0">
            <DialogTitle className="text-base">Call instructions — {name}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <DetailField label="Phone">
              {hasPhone ? (
                <div className="flex items-center gap-1 text-primary">
                  <Phone className="size-4 shrink-0" />
                  <a href={`tel:${phoneDigits}`} className="tabular-nums hover:underline">
                    {phoneDisplay}
                  </a>
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailField>

            <DetailField label="Hours">
              {scheduleText ? (
                <span>{scheduleText}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailField>

            <DetailField label="Instructions">
              {notes ? (
                <p className="whitespace-pre-wrap break-words">{notes}</p>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailField>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
